import {
  useState,
  useCallback,
  useMemo,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAppDispatch } from "../../shared/hooks";
import { unwrapResult } from "@reduxjs/toolkit";
import { setSessionChecked } from "../../store/auth/validate/validate.store";
import { selectSessionChecked } from "../../store/auth/validate/validate.selectors";
import { doTokenValidation } from "../../store/auth/validate/validate.actions";
import { doMagicTokenValidation } from "../../store/auth/validate/validate.actions";
import { getAccessToken } from "../../shared/storage";
import { setRedirectPath } from "../../store/app/app/app.store";
import { enums } from "../../shared";
import {
  MagicTokenValidationResponse,
  TokenValidationResponse,
} from "../../model/auth-response";
import {
  SIGNIN,
  DASHBOARD,
  FEEDBACK,
  COMPLETE_PROFILE,
  HOME,
  PENDING_PROFILE,
} from "../../shared/routes";
import { AUTH_CHECK_ROUTE } from "../../shared/enums";
import Text from "../UI/Text";
import { useSelector } from "react-redux";
import Loader from "../Loader";

type AuthCheckProps = {
  children: ReactNode;
  targetRoute?: AUTH_CHECK_ROUTE;
};

type LocationState = { recheckAuth?: boolean };

const AuthCheck = ({ children, targetRoute }: AuthCheckProps) => {
  const [validatingMagicToken, setValidatingMagicToken] =
    useState<boolean>(false);
  const [validatingSessionToken, setValidatingSessionToken] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");

  const location = useLocation();

  const sessionChecked = useSelector(selectSessionChecked);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const mounted = useRef(false);

  const validateTokenAndMatch = useCallback(async () => {
    if (location.pathname === HOME) {
      dispatch(setSessionChecked(true));
      return;
    } else {
      dispatch(setSessionChecked(false));
    }

    const magicToken = searchParams.get("t");

    const checkProfileCompletion = (
      response: MagicTokenValidationResponse | TokenValidationResponse
    ) => {
      let navigateRoute;

      if (response.user) {
        if (response.user.state === enums.USER_STATE.PENDING) {
          navigateRoute = PENDING_PROFILE;
        } else if (
          !(response.user.employee && response.user.employee.profileCompleted)
        ) {
          navigateRoute = COMPLETE_PROFILE;
        }
      } else if (response.employee) {
        if (!response.employee.profileCompleted) {
          navigateRoute = COMPLETE_PROFILE;
        }
      }

      if (navigateRoute) {
        dispatch(setSessionChecked(true));
        setValidatingSessionToken(false);
        return navigateRoute;
      }
    };

    const checkSession = async (validRoute?: string) => {
      const sessionToken = getAccessToken();
      if (sessionToken) {
        try {
          setValidatingSessionToken(true);
          const response = unwrapResult(
            await dispatch(doTokenValidation({ token: sessionToken }))
          );
          const navigateRoute = checkProfileCompletion(response);
          if (navigateRoute) {
            navigate(navigateRoute);
            return;
          }
          if (validRoute) {
            navigate(validRoute);
            return;
          }
          dispatch(setSessionChecked(true));
          setValidatingSessionToken(false);

          if (targetRoute === AUTH_CHECK_ROUTE.FEEDBACK_SUBMISSION) {
            const assessmentMatchId = searchParams.get("assessmentMatchId");
            const assessmentId = searchParams.get("assessmentId");
            if (!assessmentMatchId && !assessmentId) {
              setErrorMessage("No assessment match to evaluate");
            }
          } else {
            if (!location.pathname.includes(DASHBOARD)) {
              navigate(DASHBOARD);
            }
          }
        } catch (err) {
          dispatch(setSessionChecked(true));
          setValidatingSessionToken(false);
          navigate(SIGNIN);
        }
      } else {
        dispatch(setSessionChecked(true));
        setValidatingSessionToken(false);
        navigate(SIGNIN);
      }
    };

    if (magicToken) {
      try {
        setValidatingMagicToken(true);
        const response = unwrapResult(
          await dispatch(doMagicTokenValidation({ magicToken }))
        );
        const query = `${
          response.assessmentId ? `assessmentId=${response.assessmentId}` : ""
        }&${
          response.assessmentMatchId
            ? `matchId=${response.assessmentMatchId}`
            : ""
        }`;

        const navigateRoute = checkProfileCompletion(response);
        if (navigateRoute) {
          if (response.assessmentMatchId) {
            dispatch(setRedirectPath(`${FEEDBACK}?${query}`));
          }
          navigate(navigateRoute);
          return;
        }
        dispatch(setSessionChecked(true));
        if (response.assessmentMatchId) {
          dispatch(setSessionChecked(true));
          setValidatingMagicToken(false);
          navigate(`${FEEDBACK}?${query}`);
        } else {
          setValidatingMagicToken(false);
          navigate(DASHBOARD);
        }
      } catch (err: any) {
        dispatch(setSessionChecked(true));
        setValidatingMagicToken(false);
        if (err.error === enums.MAGIC_LINK_VALIDATION_ERROR.STATE_NOT_VALID) {
          setErrorMessage(
            "Your user is not yet active, please contact info@circlefeed.com"
          );
        } else if (
          err.error ===
          enums.MAGIC_LINK_VALIDATION_ERROR.MATCH_ALREADY_COMPLETED
        ) {
          checkSession(DASHBOARD);
        } else {
          checkSession();
        }
      }
    } else {
      checkSession();
    }
  }, [dispatch, searchParams, navigate, targetRoute, location.pathname]);

  const recheckAuth = useMemo(() => {
    if (location.state) {
      const { recheckAuth } = location.state as LocationState;
      return recheckAuth;
    }
    return false;
  }, [location]);

  const isLoading = useMemo(() => {
    return validatingMagicToken || validatingSessionToken;
  }, [validatingMagicToken, validatingSessionToken]);

  useEffect(() => {
    if ((!mounted.current || recheckAuth || !sessionChecked) && !isLoading)
      validateTokenAndMatch();
    if (!mounted.current) {
      mounted.current = true;
    }
  }, [sessionChecked, validateTokenAndMatch, recheckAuth, isLoading]);

  return (
    <div>
      {sessionChecked && !errorMessage ? (
        children
      ) : errorMessage ? (
        <Text textCenter>{errorMessage}</Text>
      ) : isLoading ? (
        <div
          style={{
            width: "100%",
            height: "100vh",
            display: "grid",
            placeContent: "center",
          }}
        >
          <Loader isContainerWide />
        </div>
      ) : null}
    </div>
  );
};

export default AuthCheck;
